import React, { forwardRef } from 'react'
import styled from 'styled-components'
import { useTracking } from '../../hooks/useTracking'
import DetailCard from '../common/detail-card'
import theme from '../../theme'
import {
  handoverMethodText,
  proofRecipientText,
  robotIdentityText,
  robotProof,
} from '../../lib/robot-delivery'

type Props = {}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  font-weight: 100;
  font-size: 16px;
  line-height: 21px;
  color: ${theme.midnight800};
`
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: ${theme.slate500};
`
const Label = styled.span`
  flex-shrink: 0;
`
const Value = styled.span`
  color: ${theme.midnight800};
  text-align: right;
`

/**
 * The proof that a robot order was delivered (sc-29235), shown on the delivered
 * screen in place of the courier proof block — for a robot order there is no
 * courier at the door, so no PIN or signature is ever captured and this is the
 * only proof the order has.
 *
 * Every row is individually optional: an early pilot payload missing the
 * compartment or a concierge's name renders a shorter card rather than an empty
 * row. `pin` only arrives on the phone-authenticated tracking path, the same
 * posture as the access code, so its row is simply absent on a deep link.
 */
function RobotProof(_props: Props, ref: React.Ref<HTMLDivElement>) {
  const { orderDetails } = useTracking()
  const proof = robotProof(orderDetails)

  if (!proof) {
    return null
  }

  const robotDelivery = orderDetails?.robotDelivery
  const recipient = proofRecipientText(proof)
  const robot = robotIdentityText(robotDelivery)
  const floor = robotDelivery?.floor ?? robotDelivery?.location?.floor
  const verifiedAt = proof.verifiedAt ? new Date(proof.verifiedAt) : undefined
  // An unparseable timestamp must not render "Invalid Date" as our proof.
  const deliveredAt =
    verifiedAt && !Number.isNaN(verifiedAt.getTime())
      ? verifiedAt.toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : undefined

  return (
    <DetailCard
      ref={ref}
      title={'✅ Delivered by robot'}
      data-testid="robot-proof"
    >
      <Content>
        <div data-testid="robot-proof-method">
          {handoverMethodText(proof.method)}
        </div>
        {deliveredAt ? (
          <Row>
            <Label>Delivered</Label>
            <Value data-testid="robot-proof-time">{deliveredAt}</Value>
          </Row>
        ) : null}
        {recipient ? (
          <Row>
            <Label>Received by</Label>
            <Value data-testid="robot-proof-recipient">{recipient}</Value>
          </Row>
        ) : null}
        {proof.pin ? (
          <Row>
            <Label>Code entered</Label>
            <Value data-testid="robot-proof-pin">{proof.pin}</Value>
          </Row>
        ) : null}
        {floor != null ? (
          <Row>
            <Label>Floor</Label>
            <Value data-testid="robot-proof-floor">{floor}</Value>
          </Row>
        ) : null}
        {proof.compartment ? (
          <Row>
            <Label>Compartment</Label>
            <Value data-testid="robot-proof-compartment">
              {proof.compartment}
            </Value>
          </Row>
        ) : null}
        {robot ? (
          <Row>
            <Label>Robot</Label>
            <Value data-testid="robot-proof-robot">{robot}</Value>
          </Row>
        ) : null}
      </Content>
    </DetailCard>
  )
}

export default forwardRef(RobotProof)
